import { asArray, isRecord, num, str } from './json'
import type { SurfaceGrade, SurfaceReport, SurfaceSection } from './types'

/**
 * Der Belag der Strecke: prüfen, einordnen, in Sätze fassen.
 *
 * Die Auskunft kommt vom Server (backend/internal/surface), der sie aus den
 * OSM-Knoten der berechneten Strecke nachschlägt. Hier steht, was die App
 * daraus macht - und vor allem, was sie **nicht** daraus macht.
 *
 * Der Kern ist eine einzige Unterscheidung, und sie ist der Unterschied
 * zwischen nützlich und gefährlich: eine leere Abschnittsliste heisst nur dann
 * „die Strecke ist durchgehend befestigt“, wenn auch genug nachgesehen wurde.
 * Sonst heisst sie „weiss ich nicht“. Wer beides gleich anzeigt, schickt
 * jemanden mit einem VW T3 auf gut Glück über eine Piste.
 */

/**
 * Ab welchem Anteil geprüfter Strecke die Auskunft als vollständig gilt.
 *
 * Achtzig Prozent. Die Zahl liegt niedriger als bei der Tunnelsuche
 * (ABDECKUNG_VOLLSTAENDIG in features/stages/tulipHinweise.ts, dort neunzig),
 * und das hat einen gemessenen Grund: die Abdeckung erreicht hier nie hundert
 * Prozent. Der Routendienst rechnet auf einem älteren Abzug der Karte als dem,
 * den die Abfrage sieht, und er schneidet seine Knotenkennungen ab - gemessen
 * bleiben auf einer sauber beantworteten Strecke 96 bis 98 Prozent übrig, in
 * frisch kartierten Gegenden weniger. Neunzig Prozent zu verlangen hiesse,
 * brauchbare Auskünfte als unvollständig zu kennzeichnen.
 */
export const BELAG_ABDECKUNG_VOLLSTAENDIG = 0.8

/**
 * Wie die Stufen zueinander stehen.
 *
 * Gebraucht wird die Ordnung an zwei Stellen: um die schwierigste Stelle einer
 * Etappe zu finden und um die Farbe zu wählen. Eine Zahl und kein Vergleich auf
 * Zeichenketten - „gelaende“ liegt alphabetisch vor „locker“.
 */
const RANG: Record<SurfaceGrade, number> = {
  fest: 0,
  locker: 1,
  grob: 2,
  gelaende: 3,
}

/** Die Stufen, die überhaupt eine Zeile bekommen. */
const STUFEN: SurfaceGrade[] = ['fest', 'locker', 'grob', 'gelaende']

/**
 * Wie eine Stufe im Buch und in der Liste heisst.
 *
 * Kurz, weil es neben einem Kilometerbereich steht und im fahrenden Auto
 * gelesen wird. „Naturweg“ und nicht „unbefestigte Fahrspur mit
 * eingeschränkter Befahrbarkeit“.
 */
const STUFEN_NAME: Record<SurfaceGrade, string> = {
  fest: 'befestigt',
  locker: 'Schotter',
  grob: 'Naturweg',
  gelaende: 'Gelände',
}

/**
 * Was die Stufe für einen beladenen T3 bedeutet.
 *
 * Ein Satz je Stufe, und alle vier sind Fahranweisungen und keine
 * Beschreibungen: wer das im Auto liest, will wissen, was er tun soll. Der
 * letzte ist der einzige, der von der Strecke abrät - dafür gibt es die Skala.
 */
const STUFEN_RAT: Record<SurfaceGrade, string> = {
  fest: 'Belag durchgehend.',
  locker: 'Ungebundene Decke. Tempo herunter, Abstand halten wegen Steinschlag.',
  grob: 'Unbefestigte Fahrspur. Schrittgeschwindigkeit, auf Bodenfreiheit achten.',
  gelaende: 'Die Karte verlangt hier Geländegängigkeit. Mit dem T3 besser aussen herum.',
}

/** Wahr, wenn der Wert eine Stufe ist. */
function istStufe(v: unknown): v is SurfaceGrade {
  return typeof v === 'string' && (STUFEN as string[]).includes(v)
}

/**
 * Der Belagsbericht aus der Antwort des Servers - oder keiner.
 *
 * Streng geprüft, weil er danach im Bestand liegt und mitreist. Ein einzelner
 * unbrauchbarer Abschnitt fällt für sich heraus und reisst nicht den ganzen
 * Bericht mit: eine fehlende Zeile ist eine Zeile weniger, ein verworfener
 * Bericht wäre die stille Rückkehr zu „nicht nachgesehen“.
 *
 * Die Abdeckung dagegen ist eine Bedingung. Ohne sie liesse sich nicht mehr
 * sagen, wie viel die leere Liste wert ist, und genau das ist der Kern dieser
 * Auskunft.
 *
 * @param raw Was der Server geschickt hat.
 * @param at Zeitpunkt der Abfrage.
 * @returns null, wenn die Antwort keine brauchbare Auskunft enthält.
 */
export function surfaceReportFrom(raw: unknown, at: number): SurfaceReport | null {
  if (!isRecord(raw)) return null
  const coverage = num(raw.coverage, NaN)
  if (!Number.isFinite(coverage) || coverage < 0 || coverage > 1) return null

  const sections: SurfaceSection[] = []
  for (const eintrag of asArray(raw.sections)) {
    const abschnitt = abschnittVon(eintrag)
    if (abschnitt) sections.push(abschnitt)
  }
  sections.sort((a, b) => a.fromKm - b.fromKm)

  // Die Summe wird selbst gerechnet und nicht übernommen: sie muss zu den
  // Abschnitten passen, die wirklich in der Liste stehen. Fällt einer bei der
  // Prüfung heraus, stünde sonst im Buch eine Kilometerzahl, zu der es keine
  // Zeilen gibt.
  const unpavedKm = sections.reduce((summe, a) => summe + (a.toKm - a.fromKm), 0)

  return {
    coverage,
    sections,
    unpavedKm: Math.round(unpavedKm * 10) / 10,
    checkedAt: at,
  }
}

/** Ein Abschnitt aus fremdem JSON, oder keiner. */
function abschnittVon(v: unknown): SurfaceSection | null {
  if (!isRecord(v)) return null
  const fromKm = num(v.fromKm, NaN)
  const toKm = num(v.toKm, NaN)
  const lengthM = num(v.lengthM, NaN)
  if (!Number.isFinite(fromKm) || fromKm < 0) return null
  if (!Number.isFinite(toKm) || toKm < fromKm) return null
  if (!Number.isFinite(lengthM) || lengthM <= 0) return null
  if (!istStufe(v.grade)) return null
  // „fest“ ist kein Abschnitt, sondern der Normalfall. Stünde er in der Liste,
  // wäre die Liste die ganze Etappe.
  if (v.grade === 'fest') return null

  return {
    fromKm,
    toKm,
    lengthM: Math.round(lengthM),
    grade: v.grade,
    // Die rohen Merkmale nur, wo etwas dasteht. Ein Feld mit leerer
    // Zeichenkette sieht nach dem Abgleich anders aus als ein fehlendes und ist
    // doch dasselbe.
    ...merkmal('highway', v.highway),
    ...merkmal('surface', v.surface),
    ...merkmal('tracktype', v.tracktype),
    ...merkmal('smoothness', v.smoothness),
    ...merkmal('fourwd', v.fourwd),
    ...merkmal('name', v.name),
    ...merkmal('ref', v.ref),
  }
}

/** Ein Merkmal, sofern es eines gibt. */
function merkmal(key: string, v: unknown): Record<string, string> {
  const text = str(v).trim()
  return text ? { [key]: text } : {}
}

/**
 * Wurde auf dieser Strecke wirklich nachgesehen?
 *
 * Die Frage, an der alles hängt. Ohne Bericht ist die Antwort nein; mit einem
 * Bericht, dessen Abdeckung zu niedrig ist, ebenfalls - eine leere Liste ist
 * dann keine Entwarnung, sondern eine Lücke.
 */
export function belagGeprueft(report: SurfaceReport | undefined): boolean {
  return report !== undefined && report.coverage >= BELAG_ABDECKUNG_VOLLSTAENDIG
}

/** Die schwierigste Stufe der Strecke, oder null bei einer leeren Liste. */
export function schlimmsteStufe(report: SurfaceReport | undefined): SurfaceGrade | null {
  if (!report || report.sections.length === 0) return null
  return report.sections.reduce<SurfaceGrade>(
    (bisher, a) => (RANG[a.grade] > RANG[bisher] ? a.grade : bisher),
    'fest',
  )
}

/** Wie eine Stufe heisst. */
export function stufenName(grade: SurfaceGrade): string {
  return STUFEN_NAME[grade]
}

/** Was die Stufe für einen beladenen T3 bedeutet. */
export function stufenRat(grade: SurfaceGrade): string {
  return STUFEN_RAT[grade]
}

/** Sagt, ob a schwieriger ist als b. */
export function schwierigerAls(a: SurfaceGrade, b: SurfaceGrade): boolean {
  return RANG[a] > RANG[b]
}

/**
 * Eine Länge, wie sie im Hinweis steht.
 *
 * Dieselbe Regel wie bei den Tunneln (laengeText in lib/tulipSuggest.ts): unter
 * einem Kilometer in Metern, denn „850 m“ liest sich im Auto schneller als
 * „0.9 km“, und darüber ist die Nachkommastelle die genauere Auskunft.
 */
export function belagLaenge(meter: number): string {
  const m = Math.max(0, Math.round(meter))
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${String(m)} m`
}

/**
 * Der Kilometerbereich eines Abschnitts.
 *
 * „km 34–41“ und nicht „ab km 34“: der Fahrer will wissen, wann es aufhört,
 * und ein Anfang ohne Ende ist im Auto die schlechtere Hälfte der Auskunft.
 * Ganze Kilometer, weil der Tacho eines T3 nicht mehr hergibt und weil die
 * Kilometerstände aus einer Rechnung stammen, die auf 0.2 Prozent genau ist.
 */
export function belagBereich(section: SurfaceSection): string {
  const von = Math.round(section.fromKm)
  const bis = Math.round(section.toKm)
  return von === bis ? `km ${String(von)}` : `km ${String(von)}–${String(bis)}`
}

/**
 * Der Hinweis eines Abschnitts, wie er neben der Tulpe steht.
 *
 * Stufe und Länge, dazu die Strassennummer, wenn es eine gibt: „Schotter,
 * 7.2 km“ oder „Gelände, 9.3 km (P14)“. Die Nummer ist im Auto die Auskunft, an
 * der man merkt, dass man richtig ist.
 *
 * Was hier ausdrücklich **nicht** steht, sind die rohen OSM-Merkmale. Sie
 * gehören in die Liste am Bildschirm, wo Platz ist und wo jemand nachsehen
 * will, worauf die Einstufung beruht; im Buch stünde neben einer Tulpe dann
 * „track/gravel/grade2/horrible“, und das liest im fahrenden Auto niemand.
 */
export function belagNotiz(section: SurfaceSection): string {
  const kennung = (section.ref ?? '').trim() || (section.name ?? '').trim()
  const kopf = `${stufenName(section.grade)}, ${belagLaenge(section.lengthM)}`
  return kennung ? `${kopf} (${kennung})` : kopf
}

/**
 * Der Satz über den Belag der ganzen Etappe.
 *
 * Er beantwortet die Frage, mit der jemand auf diese Maske kommt - „muss ich da
 * durch oder aussen herum“ - und er beantwortet sie auch dann, wenn die Antwort
 * „weiss ich nicht“ lautet. Genau das ist sein Zweck: die drei Fälle nicht
 * gefragt, gefragt und sauber, gefragt und lückenhaft dürfen nie gleich
 * klingen.
 */
export function belagSatz(report: SurfaceReport | undefined): string {
  if (!report) {
    return 'Noch nicht nachgesehen. Der Belag kommt aus OpenStreetMap und wird auf Knopfdruck geholt.'
  }

  const anteil = `${String(Math.round(report.coverage * 100))} %`
  if (report.sections.length === 0) {
    return belagGeprueft(report)
      ? `Durchgehend befestigt, geprüft auf ${anteil} der Strecke.`
      : `Nichts gefunden — aber nur ${anteil} der Strecke liessen sich prüfen. Das ist keine Entwarnung.`
  }

  const schlimmste = schlimmsteStufe(report)
  const zahl = report.sections.length
  const stellen = zahl === 1 ? 'eine Stelle' : `${String(zahl)} Stellen`
  const kopf = `${report.unpavedKm.toFixed(1)} km unbefestigt an ${stellen}`
  const rat = schlimmste ? ` ${stufenRat(schlimmste)}` : ''
  return belagGeprueft(report)
    ? `${kopf}, geprüft auf ${anteil} der Strecke.${rat}`
    : `${kopf} — geprüft sind nur ${anteil} der Strecke, es kann mehr sein.${rat}`
}

/**
 * Der gespeicherte Belagsbericht einer Strecke aus einer Importdatei oder aus
 * dem Abgleich.
 *
 * Fehlt er, ist es keiner: Dateien aus der Zeit vor dem Feld kennen es nicht,
 * und die meisten Strecken haben keinen. Das ist etwas anderes als eine leere
 * Abschnittsliste, und der Unterschied überlebt den Import.
 *
 * Dieselbe Prüfung wie bei der frischen Antwort des Servers, mit einem
 * Unterschied: der Zeitpunkt kommt aus der Datei und wird nicht auf jetzt
 * gesetzt. Wer eine ein Jahr alte Auskunft einliest, hat eine ein Jahr alte
 * Auskunft und keine frische.
 */
export function surfaceFromRaw(v: unknown): SurfaceReport | undefined {
  if (!isRecord(v)) return undefined
  const checkedAt = num(v.checkedAt, NaN)
  if (!Number.isFinite(checkedAt) || checkedAt <= 0) return undefined
  return surfaceReportFrom(v, checkedAt) ?? undefined
}
